import { useEffect, useMemo, useState, Fragment, type ReactNode } from 'react';
// Fully Tailwind now — toolbar controls (density / column-picker) + pagination use the ui/ kit
// (no Ant Design). Design tokens from styles/theme.css → theme-aware + dark-mode correct.
import { Button, Dropdown, Checkbox, Segmented, IconButton, Pagination } from '../ui';
import { Settings } from 'lucide-react';
import type { DataTableColumn } from './DataTable';

export type { DataTableColumn } from './DataTable';

// ── Tailwind pill (Tag replacement) — exported so converted pages can use it in cell renders ──────
const PILL: Record<string, string> = {
  default: 'bg-surface-3 text-muted border-line',
  green: 'bg-brand-500/12 text-brand-700 dark:text-brand-300 border-brand-500/25',
  blue: 'bg-sky-500/12 text-sky-600 dark:text-sky-400 border-sky-500/25',
  gold: 'bg-harvest-400/18 text-harvest-700 dark:text-harvest-300 border-harvest-400/35',
  orange: 'bg-warning-500/15 text-warning-600 dark:text-harvest-300 border-warning-500/30',
  red: 'bg-danger-500/12 text-danger-600 dark:text-danger-500 border-danger-500/30',
  purple: 'bg-violet-500/12 text-violet-600 dark:text-violet-400 border-violet-500/25',
  lime: 'bg-lime-500/15 text-lime-700 dark:text-lime-400 border-lime-500/30',
};
export function Pill({ color = 'default', className = '', children }: { color?: string; className?: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-px text-xs font-medium leading-5 ${PILL[color] ?? PILL.default} ${className}`}>
      {children}
    </span>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
      className={`h-3.5 w-3.5 shrink-0 text-subtle transition-transform duration-150 ${open ? 'rotate-90' : ''}`}>
      <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SortArrows({ dir }: { dir: 'asc' | 'desc' | null }) {
  return (
    <span className="ml-1 inline-flex flex-col leading-[0]">
      <svg viewBox="0 0 8 5" className={`h-[5px] w-2 ${dir === 'asc' ? 'text-brand-600' : 'text-line-strong'}`} fill="currentColor"><path d="M4 0l4 5H0z" /></svg>
      <svg viewBox="0 0 8 5" className={`mt-[2px] h-[5px] w-2 ${dir === 'desc' ? 'text-brand-600' : 'text-line-strong'}`} fill="currentColor"><path d="M4 5L0 0h8z" /></svg>
    </span>
  );
}

type Density = 'small' | 'middle' | 'large';
const PAD: Record<Density, string> = { small: 'px-3 py-1.5', middle: 'px-3 py-2.5', large: 'px-4 py-3.5' };

interface TwExpandable<T> {
  rowExpandable?: (r: T) => boolean;
  expandRowByClick?: boolean;
  childrenColumnName?: string;
  /** antd compat (accepted; the ui table always renders a chevron toggle column). */
  showExpandColumn?: boolean;
  defaultExpandAllRows?: boolean;
  /** Custom content rendered in a full-width row below the parent when expanded. */
  expandedRowRender?: (r: T) => ReactNode;
  /** Controlled expansion — when provided, the table reflects these keys + reports changes. */
  expandedRowKeys?: (string | number)[];
  onExpandedRowsChange?: (keys: (string | number)[]) => void;
}

interface TwDataTableProps<T> {
  storageKey: string;
  columns: DataTableColumn<T>[];
  dataSource: T[];
  rowKey: string | ((r: T, index?: number) => string);
  loading?: boolean;
  pagination?: false | { pageSize?: number; showSizeChanger?: boolean; pageSizeOptions?: number[]; hideOnSinglePage?: boolean; showTotal?: (total: number, range: [number, number]) => ReactNode; size?: 'small' | 'default'; current?: number; total?: number; onChange?: (page: number, pageSize: number) => void };
  expandable?: TwExpandable<T>;
  /** antd-style row selection (leading checkbox/radio column). Controlled via selectedRowKeys. */
  rowSelection?: {
    selectedRowKeys?: (string | number)[];
    onChange?: (selectedKeys: (string | number)[], selectedRows: T[]) => void;
    type?: 'checkbox' | 'radio';
    getCheckboxProps?: (r: T) => { disabled?: boolean };
  };
  onRow?: (r: T) => { onClick?: (e?: any) => void; style?: any; className?: string } | undefined;
  rowClassName?: (r: T) => string;
  /** antd-style footer summary — receives the page rows, returns <tr>…</tr> content. */
  summary?: (data: T[]) => ReactNode;
  toolbarLeft?: ReactNode;
  toolbarRight?: ReactNode;
  emptyText?: ReactNode;
  /** Hide the density + column-picker toolbar (for small / inline / modal tables). Default: shown. */
  showToolbar?: boolean;
  /** Drop the outer card border/shadow (for tables embedded in an already-bordered panel). */
  bordered?: boolean;
  /** Initial row density when the user hasn't chosen one (no persisted value). Lets the adapters map
   *  their antd `size` (small/middle/large) onto the density so `size="small"` actually renders dense. */
  initialDensity?: Density;
}

export function TwDataTable<T extends Record<string, any>>({
  storageKey, columns, dataSource, rowKey, loading, pagination, expandable, rowSelection, onRow, rowClassName, summary, toolbarLeft, toolbarRight, emptyText, showToolbar = true, bordered = true, initialDensity,
}: TwDataTableProps<T>) {
  const keyFor = (c: DataTableColumn<T>) =>
    c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : String(c.dataIndex ?? c.title));
  const allKeys = useMemo(() => columns.map(keyFor), [columns]);
  const defaultVisible = useMemo(() => columns.filter((c) => c.defaultVisible !== false).map(keyFor), [columns]);

  // ── Column visibility + density, persisted (same scheme as the Antd DataTable) ──
  const visKey = `gb:tbl:${storageKey}:visible`;
  const densKey = `gb:tbl:${storageKey}:density`;
  const knownKey = `gb:tbl:${storageKey}:known`;
  const [visible, setVisible] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(visKey);
      if (!raw) return defaultVisible;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return defaultVisible;
      const vis = parsed.filter((k: string) => allKeys.includes(k));
      let known: string[];
      try { const k = localStorage.getItem(knownKey); known = k ? (JSON.parse(k) || []) : vis; } catch { known = vis; }
      const fresh = defaultVisible.filter((k) => !known.includes(k) && !vis.includes(k));
      return fresh.length ? [...vis, ...fresh] : vis;
    } catch { return defaultVisible; }
  });
  const [density, setDensity] = useState<Density>(() => {
    // Only accept a valid persisted density (a stale/foreign value would make PAD[density] undefined
    // and strip all cell padding); else seed from the caller's initialDensity, else the default.
    const stored = localStorage.getItem(densKey);
    if (stored === 'small' || stored === 'middle' || stored === 'large') return stored;
    return initialDensity ?? 'middle';
  });
  useEffect(() => { localStorage.setItem(visKey, JSON.stringify(visible)); }, [visKey, visible]);
  useEffect(() => { localStorage.setItem(densKey, density); }, [densKey, density]);
  useEffect(() => {
    try {
      const k = localStorage.getItem(knownKey);
      const known: string[] = k ? (JSON.parse(k) || []) : [];
      const merged = Array.from(new Set([...known, ...allKeys]));
      if (merged.length !== known.length) localStorage.setItem(knownKey, JSON.stringify(merged));
    } catch { localStorage.setItem(knownKey, JSON.stringify(allKeys)); }
  }, [knownKey, allKeys]);
  const visibleSet = useMemo(() => new Set(visible), [visible]);
  const cols = columns.filter((c) => c.alwaysVisible || visibleSet.has(keyFor(c)));

  // ── Sorting (client-side; cycles asc → desc → off) ──
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const defaultSorter = (c: DataTableColumn<T>) => (a: T, b: T) => {
    const pick = (o: any) => (Array.isArray(c.dataIndex) ? c.dataIndex.reduce((x: any, k) => x?.[k], o) : o[c.dataIndex as string]);
    const ka = pick(a); const kb = pick(b);
    if (ka == null && kb == null) return 0;
    if (ka == null) return -1;
    if (kb == null) return 1;
    if (typeof ka === 'number' && typeof kb === 'number') return ka - kb;
    return String(ka).localeCompare(String(kb), undefined, { numeric: true });
  };
  const onSort = (c: DataTableColumn<T>) => {
    const k = keyFor(c);
    setSort((s) => (s?.key !== k ? { key: k, dir: 'asc' } : s.dir === 'asc' ? { key: k, dir: 'desc' } : null));
  };
  const sorted = useMemo(() => {
    if (!sort) return dataSource;
    const col = columns.find((c) => keyFor(c) === sort.key);
    if (!col) return dataSource;
    const sorter = col.sorter ?? defaultSorter(col);
    const arr = [...dataSource].sort(sorter);
    return sort.dir === 'desc' ? arr.reverse() : arr;
  }, [dataSource, sort, columns]);

  // ── Pagination ──
  // Two modes: CLIENT (default) — we slice `sorted` locally and own page/pageSize;
  // SERVER/controlled — the caller passes `current`/`total` (+ onChange) and hands us
  // only the current page's rows, so we render them verbatim and report page changes up.
  const pageObj = pagination === false ? undefined : pagination;
  const serverMode = !!pageObj && (pageObj.current != null || pageObj.total != null);
  const [page, setPage] = useState(1);
  const [pageSizeInternal, setPageSize] = useState<number>(pageObj?.pageSize ?? 10);
  const pageSize = serverMode ? (pageObj?.pageSize ?? pageSizeInternal) : pageSizeInternal;
  const total = serverMode ? (pageObj?.total ?? sorted.length) : sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = serverMode ? (pageObj?.current ?? 1) : Math.min(page, pageCount);
  const paged = pagination === false || serverMode ? sorted : sorted.slice((current - 1) * pageSize, current * pageSize);

  // ── Expansion (tree-data children) ──
  const childrenKey = expandable?.childrenColumnName ?? 'children';
  const controlled = expandable?.expandedRowKeys != null;
  const [openInternal, setOpenInternal] = useState<Set<string>>(new Set());
  const openSet = controlled ? new Set((expandable!.expandedRowKeys ?? []).map(String)) : openInternal;
  const getKey = (r: T, i?: number) => (typeof rowKey === 'function' ? rowKey(r, i) : String(r[rowKey]));
  const toggle = (k: string) => {
    if (controlled) {
      const cur = (expandable!.expandedRowKeys ?? []).map(String);
      const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
      expandable!.onExpandedRowsChange?.(next);
    } else {
      setOpenInternal((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
    }
  };
  const expandable_ = (r: T) => !!expandable && (expandable.rowExpandable ? expandable.rowExpandable(r) : (expandable.expandedRowRender ? true : Array.isArray((r as any)[childrenKey])));

  // ── Row selection (leading checkbox/radio column; controlled via selectedRowKeys) ──
  const selKeys = new Set((rowSelection?.selectedRowKeys ?? []).map(String));
  const selectableRows = rowSelection ? sorted.filter((r) => !rowSelection.getCheckboxProps?.(r)?.disabled) : [];
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selKeys.has(getKey(r)));
  const someSelected = !allSelected && selectableRows.some((r) => selKeys.has(getKey(r)));
  const emitSel = (keys: Set<string>) => rowSelection?.onChange?.(Array.from(keys), sorted.filter((r) => keys.has(getKey(r))));
  const toggleRow = (r: T) => {
    const k = getKey(r);
    const next = new Set(selKeys);
    if (rowSelection?.type === 'radio') { next.clear(); next.add(k); }
    else if (next.has(k)) next.delete(k); else next.add(k);
    emitSel(next);
  };
  const toggleAll = () => emitSel(allSelected ? new Set() : new Set(selectableRows.map((r) => getKey(r))));

  const cellAlign = (c: DataTableColumn<T>) => (c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left');
  const cellValue = (c: DataTableColumn<T>, r: T, i: number) => {
    const raw = c.dataIndex ? (Array.isArray(c.dataIndex) ? c.dataIndex.reduce((o: any, k) => o?.[k], r) : (r as any)[c.dataIndex]) : undefined;
    return c.render ? c.render(raw, r, i) : raw;
  };

  const renderRow = (r: T, i: number, child = false) => {
    const k = getKey(r, i);
    const canExpand = expandable_(r);
    const isOpen = openSet.has(k);
    const ro = onRow?.(r);
    const rowClick = () => {
      if (canExpand && expandable?.expandRowByClick) toggle(k);
      else ro?.onClick?.();
    };
    const clickable = (canExpand && expandable?.expandRowByClick) || !!ro?.onClick;
    return (
      <Fragment key={k}>
        <tr
          className={`border-t border-line ${child ? 'bg-surface-2/60' : 'hover:bg-surface-2'} ${clickable ? 'cursor-pointer' : ''} ${rowClassName?.(r) ?? ''} ${ro?.className ?? ''}`}
          style={ro?.style}
          onClick={clickable ? rowClick : undefined}
        >
          {rowSelection && (
            <td className={`${PAD[density]} w-10 align-middle`} onClick={(e) => e.stopPropagation()}>
              {!child && (
                <Checkbox
                  checked={selKeys.has(k)}
                  disabled={rowSelection.getCheckboxProps?.(r)?.disabled}
                  onCheckedChange={() => toggleRow(r)}
                  aria-label="Select row"
                />
              )}
            </td>
          )}
          {expandable && (
            <td className={`${PAD[density]} w-10 align-middle ${child ? 'pl-7' : ''}`} onClick={canExpand ? (e) => { e.stopPropagation(); toggle(k); } : undefined}>
              {canExpand && <Chevron open={isOpen} />}
            </td>
          )}
          {cols.map((c) => {
            const val = cellValue(c, r, i);
            if (c.ellipsis) {
              const rawTitle = typeof val === 'string' || typeof val === 'number' ? String(val) : undefined;
              return (
                <td key={keyFor(c)} className={`${PAD[density]} max-w-0 align-middle ${cellAlign(c)}`} style={c.width ? { width: c.width } : undefined}>
                  <div className="truncate" title={rawTitle}>{val}</div>
                </td>
              );
            }
            return (
              <td key={keyFor(c)} className={`${PAD[density]} align-middle ${cellAlign(c)}`} style={c.width ? { width: c.width } : undefined}>
                {val}
              </td>
            );
          })}
        </tr>
        {canExpand && isOpen && expandable?.expandedRowRender && (
          <tr><td colSpan={colSpanAll} className="border-t border-line bg-surface-2 p-0">{expandable.expandedRowRender(r)}</td></tr>
        )}
        {canExpand && isOpen && !expandable?.expandedRowRender && Array.isArray((r as any)[childrenKey]) &&
          ((r as any)[childrenKey] as T[]).map((ch, ci) => renderRow(ch, ci, true))}
      </Fragment>
    );
  };

  const colSpanAll = cols.length + (expandable ? 1 : 0) + (rowSelection ? 1 : 0);

  return (
    <div>
      {/* Toolbar */}
      {(showToolbar || toolbarLeft || toolbarRight) && (
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">{toolbarLeft}</div>
        <span className="flex-1" />
        <div className="flex flex-wrap items-center gap-2">
          {toolbarRight}
          <Segmented
            size="small" value={density} onChange={(v) => setDensity(v as Density)}
            options={[{ label: 'Compact', value: 'small' }, { label: 'Default', value: 'middle' }, { label: 'Spacious', value: 'large' }]}
          />
          <Dropdown
            trigger="click"
            align="end"
            panelClassName="p-0"
            panel={
              <div className="min-w-[210px] p-2.5">
                <div className="mb-2 px-1 text-sm font-semibold text-content">Show columns</div>
                <div className="flex flex-col gap-2 px-1">
                  {columns.map((c) => {
                    const k = keyFor(c);
                    return c.alwaysVisible ? null : (
                      <Checkbox
                        key={k}
                        checked={visible.includes(k)}
                        onCheckedChange={(chk) => setVisible((v) => (chk ? Array.from(new Set([...v, k])) : v.filter((x) => x !== k)))}
                        label={typeof c.title === 'string' && c.title ? c.title : k}
                      />
                    );
                  })}
                </div>
                <div className="mt-2.5 flex gap-3 border-t border-line px-1 pt-2.5">
                  <Button size="sm" variant="text" className="px-0! text-brand-700! hover:bg-transparent! dark:text-brand-400!" onClick={() => setVisible(allKeys)}>Show all</Button>
                  <Button size="sm" variant="text" className="px-0! text-brand-700! hover:bg-transparent! dark:text-brand-400!" onClick={() => setVisible(defaultVisible)}>Reset to default</Button>
                </div>
              </div>
            }
          >
            <IconButton diameter={32} aria-label="Column settings" className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20">
              <Settings size={16} />
            </IconButton>
          </Dropdown>
        </div>
      </div>
      )}

      {/* Table */}
      <div className={`overflow-x-auto rounded-card ${bordered ? 'border border-line bg-surface shadow-soft' : ''}`}>
        <table className="w-full border-collapse text-sm">
          <thead className="bg-surface-2">
            <tr>
              {rowSelection && (
                <th className={`${PAD[density]} w-10`}>
                  {rowSelection.type !== 'radio' && (
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleAll}
                      aria-label="Select all rows"
                    />
                  )}
                </th>
              )}
              {expandable && <th className="w-10" />}
              {cols.map((c) => {
                const dir = sort?.key === keyFor(c) ? sort.dir : null;
                return (
                  <th
                    key={keyFor(c)}
                    className={`${PAD[density]} text-2xs font-semibold uppercase tracking-wide text-muted ${cellAlign(c)} ${c.sortable || c.sorter ? 'cursor-pointer select-none hover:text-content' : ''}`}
                    style={c.width ? { width: c.width } : undefined}
                    onClick={c.sortable || c.sorter ? () => onSort(c) : undefined}
                  >
                    <span className={`inline-flex items-center ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {c.title}
                      {(c.sortable || c.sorter) && <SortArrows dir={dir} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && !dataSource.length ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-t border-line"><td colSpan={colSpanAll} className="px-3 py-3"><div className="h-6 animate-pulse rounded bg-surface-3" /></td></tr>
              ))
            ) : paged.length ? (
              paged.map((r, i) => renderRow(r, i))
            ) : (
              <tr><td colSpan={colSpanAll} className="px-4 py-12 text-center text-sm text-subtle">{emptyText ?? 'No data'}</td></tr>
            )}
          </tbody>
          {summary && paged.length > 0 && <tfoot className="bg-surface-2/60 font-semibold text-content">{summary(paged)}</tfoot>}
        </table>
      </div>

      {/* Pagination */}
      {pagination !== false && total > 0 && !(pageObj?.hideOnSinglePage && pageCount <= 1) && (
        <div className="mt-3 flex items-center justify-end">
          <Pagination
            size="small"
            current={current}
            pageSize={pageSize}
            total={total}
            showSizeChanger={pageObj?.showSizeChanger ?? true}
            pageSizeOptions={pageObj?.pageSizeOptions}
            onChange={(p, ps) => { if (serverMode) { pageObj?.onChange?.(p, ps); } else { setPage(p); setPageSize(ps); } }}
            showTotal={pageObj?.showTotal ?? ((t) => `${t} rows`)}
          />
        </div>
      )}
    </div>
  );
}
